import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { notificationAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Bell,
  Mail,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  Star,
  UserPlus,
  Activity,
  Loader2,
  Save,
} from "lucide-react";
import { motion } from "framer-motion";

interface EmailPreferences extends Record<string, boolean> {
  TICKET_CREATED: boolean;
  TICKET_ASSIGNED: boolean;
  STATUS_CHANGED: boolean;
  COMMENT_ADDED: boolean;
  TICKET_RESOLVED: boolean;
  FEEDBACK_REQUESTED: boolean;
  SLA_BREACH_WARNING: boolean;
}

export default function NotificationSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<EmailPreferences>({
    TICKET_CREATED: true,
    TICKET_ASSIGNED: true,
    STATUS_CHANGED: true,
    COMMENT_ADDED: true,
    TICKET_RESOLVED: true,
    FEEDBACK_REQUESTED: true,
    SLA_BREACH_WARNING: true,
  });

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.getEmailPreferences();

      if (response.success && response.data) {
        setPreferences(response.data);
      }
    } catch (error) {
      toast.error("Failed to load notification preferences");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: keyof EmailPreferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response =
        await notificationAPI.updateEmailPreferences(preferences);

      if (response.success) {
        toast.success("Preferences Updated", {
          description: "Your email notification settings have been saved",
        });
      } else {
        throw new Error("Failed to update preferences");
      }
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const notificationTypes = [
    {
      key: "TICKET_CREATED" as keyof EmailPreferences,
      icon: Bell,
      title: "Ticket Created",
      description: "Get notified when new tickets are created",
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      roles: ["admin"],
    },
    {
      key: "TICKET_ASSIGNED" as keyof EmailPreferences,
      icon: UserPlus,
      title: "Ticket Assigned",
      description: "Receive alerts when a ticket is assigned to you",
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      roles: ["technician"],
    },
    {
      key: "STATUS_CHANGED" as keyof EmailPreferences,
      icon: Activity,
      title: "Status Changed",
      description: "Get updates when ticket status changes",
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10",
      roles: ["admin", "technician", "user"],
    },
    {
      key: "COMMENT_ADDED" as keyof EmailPreferences,
      icon: MessageSquare,
      title: "New Comments",
      description: "Be notified of new comments on your tickets",
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      roles: ["admin", "technician", "user"],
    },
    {
      key: "TICKET_RESOLVED" as keyof EmailPreferences,
      icon: CheckCircle,
      title: "Ticket Resolved",
      description: "Receive confirmation when your ticket is resolved",
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      roles: ["user"],
    },
    {
      key: "FEEDBACK_REQUESTED" as keyof EmailPreferences,
      icon: Star,
      title: "Feedback Requests",
      description:
        "Get requests to rate resolved tickets (sent 1 hour after resolution)",
      color: "text-yellow-500",
      bgColor: "bg-yellow-500/10",
      roles: ["user"],
    },
    {
      key: "SLA_BREACH_WARNING" as keyof EmailPreferences,
      icon: AlertTriangle,
      title: "SLA Breach Warnings",
      description: "Urgent alerts for tickets approaching deadlines",
      color: "text-red-500",
      bgColor: "bg-red-500/10",
      roles: ["technician"],
    },
  ];

  const visibleNotifications = notificationTypes.filter((type) =>
    type.roles.includes(user?.role || ""),
  );

  return (
    <DashboardLayout>
      <div className="min-h-full p-4 sm:p-6 md:p-8 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 font-serif">
                Email Notification Settings
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Control which email notifications you receive
              </p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-border/50 rounded-xl p-6 shadow-lg"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">
                Email Notifications
              </h2>
              <p className="text-sm text-muted-foreground">
                Professional HTML emails sent to your registered email address
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4">
              {visibleNotifications.map((type, idx) => (
                <motion.div
                  key={type.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + idx * 0.05 }}
                  className="flex items-start justify-between p-4 bg-muted/30 rounded-lg border border-border/30 hover:border-primary/50 transition-all"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`p-3 ${type.bgColor} rounded-lg`}>
                      <type.icon className={`w-5 h-5 ${type.color}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground mb-1">
                        {type.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {type.description}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggle(type.key)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 cursor-pointer focus:ring-primary focus:ring-offset-2 ${
                      preferences[type.key]
                        ? "bg-primary"
                        : "bg-muted-foreground/30"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences[type.key]
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card border border-border/50 rounded-xl p-6 shadow-lg"
        >
          <h3 className="text-lg font-bold text-foreground mb-4">
            About Email Notifications
          </h3>
          <div className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong className="text-foreground">
                  Professional Design:
                </strong>{" "}
                All emails use branded HTML templates with CamPulse styling
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong className="text-foreground">Smart Filtering:</strong>{" "}
                You won't receive emails for actions you performed yourself
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong className="text-foreground">Mobile Friendly:</strong>{" "}
                Responsive design works on all devices and email clients
              </p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong className="text-foreground">Quick Actions:</strong> Each
                email includes buttons to view tickets and take action
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
